import { ExtraWorkedTimeType } from "@src/appV2/Shifts/Shift/types";
import { z } from "zod";

import { type TimesheetStep } from "../../../ShiftDetails/Timekeeping/hooks/useTimesheetStep";

interface CreateSubmitTimesheetFormSchemaOptions {
  isCaliforniaFacility: boolean;
  currentStep?: TimesheetStep;
  isQrCodeSubmission?: boolean;
  isUnverifiedTimesheetFlow?: boolean;
  isSkipWorkplaceSignatureFlow?: boolean;
}

export function createSubmitTimesheetFormSchema(options: CreateSubmitTimesheetFormSchemaOptions) {
  const {
    isCaliforniaFacility,
    currentStep,
    isQrCodeSubmission = false,
    isUnverifiedTimesheetFlow = false,
    isSkipWorkplaceSignatureFlow = false,
  } = options;
  const isHcpSigningStep = currentStep === "hcp-signing";
  // When skipping workplace signature or QR code submission, signature-related fields are optional for validation
  const isSignatureRequired = !isQrCodeSubmission && !isSkipWorkplaceSignatureFlow;
  const isWorkerStationRequired = isCaliforniaFacility && !isSkipWorkplaceSignatureFlow;
  const isWorkerNursingAssignmentRequired = isCaliforniaFacility && !isSkipWorkplaceSignatureFlow;
  const isFacilityEmployeeNameRequired = !isSkipWorkplaceSignatureFlow;

  // Base schema matching TimesheetFormValues interface exactly
  // Fields marked optional here must match the interface to maintain type compatibility
  return z.object({
    facilityEmployeeName: isFacilityEmployeeNameRequired
      ? z.string().min(1, "Please enter workplace employee name")
      : z.string().optional(),
    // Make signature optional for QR code submissions
    facilityEmployeeSignature: isSignatureRequired
      ? z.string().min(1, "Please provide workplace employee signature")
      : z.string().optional(),
    workerStation: isWorkerStationRequired
      ? z.string().min(1, "Please enter worker station")
      : z.string().optional(),
    workerNursingAssignment: isWorkerNursingAssignmentRequired
      ? z.string().min(1, "Please enter nursing assignment")
      : z.string().optional(),
    clockInTime: z.date(),
    clockOutTime: z.date(),
    breakStartTime: z.date().optional(),
    breakEndTime: z.date().optional(),
    breakAuthorizationReason: z.string().optional(),
    missedPunches: isUnverifiedTimesheetFlow
      ? z
          .array(
            z.object({
              type: z.literal(ExtraWorkedTimeType.FULL_SHIFT),
              reason: z
                .string()
                .trim()
                .min(1, "Please explain why you couldn't clock in/out normally")
                .min(60, "Please provide at least 60 characters"),
            })
          )
          .min(1, "At least one missed punch reason is required")
      : z
          .array(
            z.object({
              type: z.literal(ExtraWorkedTimeType.BREAK),
              reason: z.string(),
            })
          )
          .optional(),
    // California HCP fields - only required during HCP signing step
    hcpName:
      isCaliforniaFacility && isHcpSigningStep
        ? z.string().min(1, "Please enter your full name")
        : z.string().optional(),
    hcpSignature:
      isCaliforniaFacility && isHcpSigningStep
        ? z.string().min(1, "Please provide your signature")
        : z.string().optional(),
    // QR code specific fields
    timecard: isQrCodeSubmission
      ? z.object({
          url: z.string(),
          filename: z.string(),
          fileStorageFileKey: z.string(),
        })
      : z
          .object({
            url: z.string(),
            filename: z.string(),
            fileStorageFileKey: z.string(),
          })
          .optional(),
  });
}

export type SubmitTimesheetFormValues = z.infer<ReturnType<typeof createSubmitTimesheetFormSchema>>;
